import { useId, useState } from "react";
import { observer } from "mobx-react";
import { IconCheck, IconChevronDown, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "@/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { TSkill } from "@/types/skill";

export interface SkillsPickerProps {
  /** All assignable skills (already filtered to the active/non-archived set). */
  skills: TSkill[];
  /** Currently assigned skill ids. */
  selectedIds: Set<string>;
  /** Persist the next full set of assigned ids (add/remove is computed here). */
  onChange: (nextIds: string[]) => void | Promise<void>;
  /** Disable toggling while a save is in flight. */
  isSaving?: boolean;
  /** Why the viewer may not change the selection. Set, the trigger is disabled
   *  and the reason shows under it as text, which touch and keyboard users reach
   *  too (a disabled trigger takes no focus, so a tooltip would not). */
  disabledReason?: string;
}

/**
 * Controlled, presentational skills multi-select popover. Renders the trigger
 * (selected chips) + a searchable option list; the caller owns the data source
 * and persistence (`onChange` receives the next full id set). Shared by the
 * personal skills panel (`MySkillsPicker`) and the on-behalf member Skills tab
 * (`MemberSkillsPicker`) so both read identically.
 */
export const SkillsPicker = observer(function SkillsPicker({
  skills,
  selectedIds,
  onChange,
  isSaving,
  disabledReason,
}: SkillsPickerProps) {
  const { t } = useTranslation();
  const reasonId = useId();
  const disabled = disabledReason !== undefined;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedSkills = skills.filter((s) => selectedIds.has(s.id));
  const query = search.trim().toLowerCase();
  const filteredSkills = query
    ? skills.filter((s) => s.name.toLowerCase().includes(query))
    : skills;

  const handleToggle = (skillId: string) => {
    const currentIds = Array.from(selectedIds);
    const nextIds = selectedIds.has(skillId)
      ? currentIds.filter((id) => id !== skillId)
      : [...currentIds, skillId];
    void onChange(nextIds);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  return (
    <>
      <Popover open={!disabled && open} onOpenChange={disabled ? undefined : handleOpenChange}>
        <PopoverTrigger
          type="button"
          disabled={disabled}
          aria-describedby={disabled ? reasonId : undefined}
          className="flex min-h-9 w-full max-w-md items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-background"
        >
          <div className="flex flex-wrap gap-1">
            {selectedSkills.length === 0 ? (
              <span className="text-muted-foreground">{t("profile.skills.placeholder")}</span>
            ) : (
              selectedSkills.map((skill) => (
                <span
                  key={skill.id}
                  className="inline-flex items-center rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs text-foreground"
                >
                  {skill.name}
                </span>
              ))
            )}
          </div>
          <IconChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </PopoverTrigger>
        <PopoverContent size="auto" align="start" className="w-(--anchor-width) max-w-md p-2">
          <div className="flex items-center gap-2 rounded-sm border border-border bg-background px-2">
            <IconSearch className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("profile.skills.search_placeholder")}
              className="w-full bg-transparent py-1 text-sm placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
          <div className="mt-2 max-h-48 space-y-0.5 overflow-y-auto">
            {filteredSkills.length > 0 ? (
              filteredSkills.map((skill) => {
                const isSelected = selectedIds.has(skill.id);
                return (
                  <button
                    key={skill.id}
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleToggle(skill.id)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-1.5 py-1.5 text-left text-sm hover:bg-accent",
                      isSelected ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                      {isSelected && <IconCheck className="h-3.5 w-3.5" />}
                    </span>
                    <span className="truncate">{skill.name}</span>
                  </button>
                );
              })
            ) : (
              <p className="px-1.5 py-1.5 text-sm text-muted-foreground">
                {t("profile.skills.empty_search")}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {disabled ? (
        <p id={reasonId} className="mt-2 text-xs text-muted-foreground">
          {disabledReason}
        </p>
      ) : null}
    </>
  );
});
